import { defineStore } from 'pinia'
import { type IApiInstance } from '~/plugins/api'
import {
  EpisodeOfCareStatus,
  type IIncidentAlert,
  type IPriorityPatient,
  type IPriorityPatientEpisodeOfCare,
  type IPriorityPatientIMonitor,
  type IPriorityPatientIncident,
  type IPriorityPatientPatient,
  type IPriorityPatientPatientInformation,
} from '~/repository/types/priorityPatients'
import type { IPractitionerPatientsCounterDto } from '~/repository/types/tasks'
import { computePriorityPatientItem } from '~/utils/patients/computePriorityPatientItem'

interface IEncounterCounter {
  inProgress: number
  patientId: string
}

interface IPriotityPatients extends IPriorityPatient {
  taskCounters: IPractitionerPatientsCounterDto[]
  encountersInProgress: number
  todayOrBeforeTotal: number
  inNextDaysTotal: number
  laterThisMonthTotal: number
  todayOrBeforeForRequester: number
  inNextDaysForRequester: number
  laterThisMonthForRequester: number
  todayOrBeforeForRequesterTeams: []
  inNextDaysForRequesterTeams: []
  laterThisMonthForRequesterTeams: []
  severity: number
}

export type IPriorityPatientsState = {
  isLoaded: boolean
  priorityPatients: IPriotityPatients[]
  filteredPriorityPatients: IPriotityPatients[]
  selectedPatientId: string | null
}

const transfromPriorityPatient = (taskCounters: any, encounterCounters: IEncounterCounter[]) => (priorityPatient: any) => {
  const counters = taskCounters.filter((counter: any) => counter.patientId === priorityPatient.patient.id)
  const patientEncounterCount = encounterCounters?.find(({ patientId }) => patientId === priorityPatient.patient.id)
  let todayOrBeforeTotal: number = 0 // Late + High
  let inNextDaysTotal: number = 0 // Medium
  let laterThisMonthTotal: number = 0 // Low + Normal
  let todayOrBeforeForRequester: number = 0
  let inNextDaysForRequester: number = 0
  let laterThisMonthForRequester: number = 0
  let todayOrBeforeForRequesterTeams = []
  let inNextDaysForRequesterTeams = []
  let laterThisMonthForRequesterTeams = []
  try {
    for (let i = 0; i < counters.length; i++) {
      todayOrBeforeTotal += (counters[i]?.counters?.[0]?.total || 0) + (counters[i]?.counters?.[1]?.total || 0)
      inNextDaysTotal += counters[i]?.counters?.[2]?.total || 0
      laterThisMonthTotal += (counters[i]?.counters?.[3]?.total || 0) + (counters[i]?.counters?.[4]?.total || 0)
      todayOrBeforeForRequester +=
        (counters[i]?.counters?.[0]?.forRequester || 0) + (counters[i]?.counters?.[1]?.forRequester || 0)
      inNextDaysForRequester += counters[i]?.counters?.[2]?.forRequester || 0
      laterThisMonthForRequester +=
        (counters[i]?.counters?.[3]?.forRequester || 0) + (counters[i]?.counters?.[4]?.forRequester || 0)
      if (
        counters[i]?.counters?.[0]?.forRequesterTeams &&
        Object.keys(counters[i]?.counters?.[0]?.forRequesterTeams)?.length
      ) {
        todayOrBeforeForRequesterTeams.push(...Object.keys(counters[i]?.counters?.[0]?.forRequesterTeams))
        todayOrBeforeForRequesterTeams.push(...Object.keys(counters[i]?.counters?.[1]?.forRequesterTeams))
      }
      if (
        counters[i]?.counters?.[2]?.forRequesterTeams &&
        Object.keys(counters[i]?.counters?.[2]?.forRequesterTeams)?.length
      ) {
        inNextDaysForRequesterTeams.push(...Object.keys(counters[i]?.counters?.[2]?.forRequesterTeams))
      }
      if (
        counters[i]?.counters?.[3]?.forRequesterTeams &&
        Object.keys(counters[i]?.counters?.[3]?.forRequesterTeams)?.length
      ) {
        laterThisMonthForRequesterTeams.push(...Object.keys(counters[i]?.counters?.[3]?.forRequesterTeams))
        laterThisMonthForRequesterTeams.push(...Object.keys(counters[i]?.counters?.[4]?.forRequesterTeams))
      }
    }
  } catch (error) {
    console.error('Error in transformPriorityPatient:', error)
  }

  return {
    ...priorityPatient,
    taskCounters: counters,
    encountersInProgress: patientEncounterCount?.inProgress || 0,
    todayOrBeforeTotal,
    inNextDaysTotal,
    laterThisMonthTotal,
    todayOrBeforeForRequester,
    inNextDaysForRequester,
    laterThisMonthForRequester,
    todayOrBeforeForRequesterTeams,
    inNextDaysForRequesterTeams,
    laterThisMonthForRequesterTeams,
  }
}

const fetchNextPagePriorityPatient = (
  api: any,
  nextPageUrl: string,
  patchThis: any,
  priorityPatients: any,
  taskCounters: any,
  encounterCounter: any,
) => {
  api.priorityPatients.getNextPage(nextPageUrl).then((response: any) => {
    if (response.items && response.items.length > 0) priorityPatients = [...priorityPatients, ...response.items]
    patchThis.$patch({
      priorityPatients: priorityPatients.map(transfromPriorityPatient(taskCounters, encounterCounter)),
      isLoaded: true,
    })
    if (response.nextPageUrl) {
      fetchNextPagePriorityPatient(api, response.nextPageUrl, patchThis, priorityPatients, taskCounters, encounterCounter)
    }
  })
}

export const usePriorityPatientsStore = defineStore('priorityPatients', {
  persist: true,
  state: (): IPriorityPatientsState => ({
    isLoaded: false,
    priorityPatients: [],
    filteredPriorityPatients: [],
    selectedPatientId: null,
  }),
  getters: {
    getPriorityPatientById: (state) => (id: string) => state?.priorityPatients.find((p) => p.patient.id === id),
    getPatient:
      (state) =>
      (id: string): IPriorityPatientPatient | undefined => {
        return state?.priorityPatients?.find((p) => p.patient.id === id)?.patient
      },
    getPatientInformation:
      (state) =>
      (id: string): IPriorityPatientPatientInformation | undefined => {
        return state.priorityPatients.find((p) => p.patient.id === id)?.patientInformation
      },
    getEpisodeOfCare:
      (state) =>
      (id: string): IPriorityPatientEpisodeOfCare | undefined => {
        return state.priorityPatients.find((p) => p.patient.id === id)?.episodeOfCare
      },
    getIncident:
      (state) =>
      (id: string): IPriorityPatientIncident | null => {
        return state.priorityPatients.find((p) => p.patient.id === id)?.incident || null
      },
    getMonitor:
      (state) =>
      (id: string): IPriorityPatientIMonitor | undefined => {
        return state.priorityPatients.find((p) => p.patient.id === id)?.monitor
      },
    getAlerts:
      (state) =>
      (id: string): IIncidentAlert[] => {
        return state.priorityPatients.find((p) => p.patient.id === id)?.alerts || []
      },
    getTaskCounters:
      (state) =>
      (id: string): IPractitionerPatientsCounterDto[] => {
        return state.priorityPatients.find((p) => p.patient.id === id)?.taskCounters || []
      },
    getTodayOrBeforeTotal:
      (state) =>
      (id: string): number => {
        return state.priorityPatients.find((p) => p.patient.id === id)?.todayOrBeforeTotal || 0
      },
    getInNextDaysTotal:
      (state) =>
      (id: string): number => {
        return state.priorityPatients.find((p) => p.patient.id === id)?.inNextDaysTotal || 0
      },
    getSeverity:
      (state) =>
      (id: string): number => {
        const priorityPatient = state.priorityPatients.find((p) => p.patient.id === id)
        const transformValue = priorityPatient && computePriorityPatientItem(priorityPatient)
        return transformValue?.severity || 0
      },
    getLaterThisMonth:
      (state) =>
      (id: string): number => {
        return state.priorityPatients.find((p) => p.patient.id === id)?.laterThisMonthTotal || 0
      },
    getEncountersInProgress:
      (state) =>
      (id: string): number => {
        return state.priorityPatients.find((p) => p.patient.id === id)?.encountersInProgress || 0
      },
  },
  actions: {
    async load(options: { episodeOfCareStatuses?: EpisodeOfCareStatus[]; showAllPatients?: boolean } = {}) {
      const api: IApiInstance = useNuxtApp().$api as IApiInstance
      if (this.priorityPatients.length === 0) this.$patch({ isLoaded: false })

      try {
        let priorityPatientsResult = await api.priorityPatients.get(options)
        let priorityPatients = priorityPatientsResult.items
        this.$patch({
          priorityPatients: priorityPatients.map(transfromPriorityPatient([], [])),
          isLoaded: true,
        })
        const taskCounters = await api.tasks.getPractitionerTaskCount()
        const encounterCounter = await api.encounters.getEncounterCounters()
        if (priorityPatientsResult.nextPageUrl) {
          fetchNextPagePriorityPatient(
            api,
            priorityPatientsResult.nextPageUrl,
            this,
            priorityPatients,
            taskCounters,
            encounterCounter,
          )
        } else {
          this.$patch({
            priorityPatients: priorityPatients.map(transfromPriorityPatient(taskCounters, encounterCounter)),
          })
        }
      } catch (error) {
        this.$patch({ isLoaded: true })
      }
    },
    async updateTaskAndEncounter() {
      const api: IApiInstance = useNuxtApp().$api as IApiInstance
      const taskCounters = await api.tasks.getPractitionerTaskCount()
      const encounterCounters = await api.encounters.getEncounterCounters()
      this.$patch({
        priorityPatients: this.priorityPatients.map(transfromPriorityPatient(taskCounters, encounterCounters)),
      })
    },
    reloadFromWorker(data: any) {
      try {
        const priorityPatients = data[0]
        const taskCounters = data[1]
        const encounterCounters = data[2]
        this.priorityPatients = priorityPatients.map(transfromPriorityPatient(taskCounters, encounterCounters))
      } catch (error) {}
    },
    setPatientSeverity(patientId: string, incident: any) {
      this.$patch((state) => {
        const index = state.priorityPatients.findIndex((p) => p.patient.id === patientId)
        state.priorityPatients[index].incident = incident
      })
    },
    setPatientCoordinates(patientId: string, coordinates: any) {
      this.$patch((state) => {
        const index = state.priorityPatients.findIndex((p) => p.patient.id === patientId)
        state.priorityPatients[index].livingPlace.geolocation = { latitude: coordinates[1], longitude: coordinates[0] }
      })
    },
    setPatientInformation(patientId: string, data: any) {
      this.$patch((state) => {
        const index = state.priorityPatients.findIndex((p) => p.patient.id === patientId)
        state.priorityPatients[index].patient = { ...state.priorityPatients[index].patient, ...data }
      })
    },
    setPatientRPI(patientId: string, data: any) {
      this.$patch((state) => {
        const index = state.priorityPatients.findIndex((p) => p.patient.id === patientId)
        if (state.priorityPatients[index]) state.priorityPatients[index].patientInformation.rpi = data.rpi
      })
    },
    setFilteredList(list: []) {
      this.$patch((state) => {
        state.filteredPriorityPatients = list
      })
    },
    async selectPatient(patientId: string) {
      this.$patch({ selectedPatientId: patientId })
    },
    clear() {
      this.$reset()
    },
  },
})
